import { Switch } from "@/components/ui/switch";
import {
  useBroadcastSettings,
  useSetBroadcastAuthorization,
} from "@/hooks/useBroadcasts";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/**
 * The school's own authorization to spend its Meta budget on bulk sends.
 *
 * A bordered card with a Switch rather than a checkbox next to "Mostrar
 * archivadas": this is a persisted policy that lets any advisor spend real
 * money, and it must not read as another view filter.
 *
 * Rendered for EVERY role and disabled unless the viewer can change it — a
 * member seeing the state and that they cannot change it is the answer to "why
 * can't I send?". (In practice only admins reach this page, but the component
 * does not assume that.)
 */
export function BroadcastAuthorizationCard(): React.JSX.Element | null {
  const { data: settings, isPending } = useBroadcastSettings();
  const setAuthorization = useSetBroadcastAuthorization();

  if (isPending || !settings) return null;

  return (
    <div className="mt-6 rounded-lg border border-ink-100 bg-white p-4">
      <div className="flex items-start gap-3">
        <Switch
          checked={settings.enabled}
          disabled={!settings.canManage || setAuthorization.isPending}
          onCheckedChange={(checked) => {
            setAuthorization.mutate(checked);
          }}
          aria-label="Permitir envíos masivos"
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink-900">
            Permitir que los miembros de {settings.clientName} envíen mensajes masivos
            con costo extra de Meta
          </p>
          <p className="mt-1 text-sm text-ink-700">
            Cuando está activo, cualquier persona del equipo puede enviar una plantilla
            aprobada a todos los prospectos que coincidan con los filtros del Pipeline.
            Máximo {settings.maxRecipients} por envío. Meta cobra cada mensaje
            entregado.
          </p>

          {settings.enabled && settings.enabledAt ? (
            <p className="mt-2 text-xs text-ink-500">
              Activado
              {settings.enabledByName ? ` por ${settings.enabledByName}` : ""} el{" "}
              {formatDate(settings.enabledAt)}.
            </p>
          ) : null}

          {/* The transport gate is separate on purpose: an upgrade fixes the
              plan, and nothing but migrating off Baileys fixes this one. Saying
              so here keeps an admin from flipping the switch and wondering why
              the button still never appears. */}
          {settings.enabled && !settings.transportOk ? (
            <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Este cliente todavía no está conectado a la API oficial de WhatsApp, así
              que los envíos masivos no se pueden realizar aunque el permiso esté
              activo.
            </p>
          ) : null}

          {!settings.canManage ? (
            <p className="mt-2 text-xs text-ink-500">
              Solo un administrador puede cambiar este permiso.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
